<template>
  <div class="support-tickets">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
      <div>
        <h2 class="heading-4" style="color: var(--text-primary);">
          Your Tickets
        </h2>
        <p class="text-body-sm mt-1" style="color: var(--text-secondary);">
          View and manage your support requests
        </p>
      </div>
      <button
        @click="openNewTicketModal"
        class="btn btn-primary">
        <svg class="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        New Ticket
      </button>
    </div>

    <!-- Tabs -->
    <div class="tabs mb-6">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        @click="currentTab = tab.key"
        :class="['tab', { active: currentTab === tab.key }]">
        {{ tab.label }}
        <span
          v-if="tab.count > 0"
          class="ml-2 badge"
          :class="currentTab === tab.key ? 'badge-primary' : 'badge-default'">
          {{ tab.count }}
        </span>
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="card">
      <div class="divide-y" style="border-color: var(--border-default);">
        <div v-for="n in 3" :key="n" class="p-6">
          <div class="animate-pulse">
            <div class="flex items-start justify-between">
              <div class="flex-1">
                <div class="h-3 rounded w-24 mb-2" style="background-color: var(--bg-muted);" />
                <div class="h-4 rounded w-3/4 mb-2" style="background-color: var(--bg-muted);" />
                <div class="h-3 rounded w-1/2" style="background-color: var(--bg-muted);" />
              </div>
              <div class="h-6 w-20 rounded-full" style="background-color: var(--bg-muted);" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else-if="filteredTickets.length === 0" class="card">
      <div class="empty-state py-16">
        <div class="empty-state-icon">
          <svg class="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
        </div>
        <h3 class="empty-state-title">
          No tickets found
        </h3>
        <p class="empty-state-description">
          {{ currentTab === 'all' ? 'Create your first support ticket to request updates or get help.' : 'No tickets in this category.' }}
        </p>
        <button
          v-if="currentTab === 'all'"
          @click="openNewTicketModal"
          class="btn btn-primary mt-4">
          <svg class="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          Create Ticket
        </button>
      </div>
    </div>

    <!-- Ticket List -->
    <div v-else class="card">
      <div class="divide-y" style="border-color: var(--border-default);">
        <div
          v-for="ticket in filteredTickets"
          :key="ticket.id"
          @click="$emit('select-ticket', ticket)"
          class="p-6 cursor-pointer transition-colors hover:bg-gray-50">
          <div class="flex items-start justify-between gap-4">
            <!-- Main content -->
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 mb-1">
                <span class="text-caption font-mono" style="color: var(--text-tertiary);">{{ ticket.ticket_number }}</span>
                <span :class="['badge', categoryBadgeClass(ticket.category)]">
                  {{ categoryLabel(ticket.category) }}
                </span>
              </div>
              <h3 class="text-body font-medium mb-1" style="color: var(--text-primary);">
                {{ ticket.subject }}
              </h3>
              <p
                v-if="ticket.last_message_preview"
                class="text-body-sm mb-2"
                style="color: var(--text-secondary); display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;">
                {{ ticket.last_message_preview }}
              </p>
              <div class="flex items-center gap-4 text-caption" style="color: var(--text-tertiary);">
                <span class="flex items-center gap-1">
                  <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {{ formatDate(ticket.updated_at) }}
                </span>
                <span v-if="ticket.message_count > 0" class="flex items-center gap-1">
                  <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                  {{ ticket.message_count }}
                </span>
              </div>
            </div>

            <!-- Status & arrow -->
            <div class="flex items-center gap-3 flex-shrink-0">
              <span :class="['badge', statusBadgeClass(ticket.status)]">
                {{ statusLabel(ticket.status) }}
              </span>
              <svg class="w-5 h-5" style="color: var(--text-muted);" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- New Ticket Modal -->
    <new-ticket-modal
      ref="newTicketModal"
      :website-id="websiteId"
      @ticket-created="onTicketCreated" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { getSupportTickets } from '~/javascript/api/support_tickets.js'
// eslint-disable-next-line no-unused-vars
import NewTicketModal from './new_ticket_modal.vue'

// Props
const props = defineProps({
  websiteId: {
    type: String,
    required: true
  }
})

// Emits
const emit = defineEmits(['select-ticket'])

// Refs
const newTicketModal = ref(null)

// State
const tickets = ref([])
const loading = ref(true)
const currentTab = ref('all')

// Computed
const tabs = computed(() => [
  { key: 'all', label: 'All', count: tickets.value.length },
  { key: 'open', label: 'Open', count: tickets.value.filter((t) => ['open', 'in_progress', 'waiting_on_customer'].includes(t.status)).length },
  { key: 'closed', label: 'Resolved', count: tickets.value.filter((t) => ['resolved', 'closed'].includes(t.status)).length }
])

const filteredTickets = computed(() => {
  if (currentTab.value === 'all') return tickets.value
  if (currentTab.value === 'open') {
    return tickets.value.filter((t) => ['open', 'in_progress', 'waiting_on_customer'].includes(t.status))
  }
  if (currentTab.value === 'closed') {
    return tickets.value.filter((t) => ['resolved', 'closed'].includes(t.status))
  }
  return tickets.value
})

// Methods
const fetchTickets = async() => {
  try {
    loading.value = true
    const response = await getSupportTickets({ website_id: props.websiteId })
    tickets.value = response.data.tickets
  } catch (error) {
    console.error('Error fetching tickets:', error) // eslint-disable-line no-console
  } finally {
    loading.value = false
  }
}

const openNewTicketModal = () => {
  newTicketModal.value?.open()
}

const onTicketCreated = (ticket) => {
  tickets.value.unshift(ticket)
  emit('select-ticket', ticket)
}

const formatDate = (date) => {
  if (!date) return 'Unknown'
  const d = new Date(date)
  const now = new Date()
  const diffInMinutes = Math.floor((now - d) / 1000 / 60)

  if (diffInMinutes < 1) return 'Just now'
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`
  if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`
  if (diffInMinutes < 10080) return `${Math.floor(diffInMinutes / 1440)}d ago`
  return d.toLocaleDateString()
}

const statusLabel = (status) => {
  const labels = {
    open: 'Open',
    in_progress: 'In Progress',
    waiting_on_customer: 'Awaiting Reply',
    resolved: 'Resolved',
    closed: 'Closed'
  }
  return labels[status] || status
}

const statusBadgeClass = (status) => {
  const classes = {
    open: 'badge-primary',
    in_progress: 'badge-warning',
    waiting_on_customer: 'badge-default',
    resolved: 'badge-success',
    closed: 'badge-dark'
  }
  return classes[status] || 'badge-default'
}

const categoryLabel = (category) => {
  const labels = {
    content_change: 'Content Change',
    bug_report: 'Bug Report',
    question: 'Question',
    feature_request: 'Feature Request'
  }
  return labels[category] || category
}

const categoryBadgeClass = () => 'badge-default'

// Expose methods for parent
defineExpose({ fetchTickets })

// Lifecycle
onMounted(() => {
  fetchTickets()
})
</script>
